/**
 * The conversation: what has been asked, what is in flight, and how to add to it.
 *
 * Split from App.tsx, which is the shell — the layout, the header, the composer
 * (`composer.tsx`) and the output window. This file is the exchange machinery behind them: open a stream,
 * fold each event into the last exchange, and hold the history the relay does not.
 *
 * History lives in the browser because the relay is stateless. That is what lets the
 * API scale without a session store, and it is the cheapest protection there is for
 * what is GDPR Article 9 data: a question nobody stored is a question nobody can leak.
 */

import { useCallback, useRef, useState } from 'react'

import type { ChatEvent, Turn } from './api'
import { ask } from './api'
import { setAfterAnswer } from './activeVersions'
import { isWorking, type Exchange } from './exchange'
import { t } from './i18n'
import type { Mode } from './mode'

/**
 * Stream one answer into the exchange already on screen.
 *
 * Module-level rather than inside the hook so the hook stays readable: this is the whole
 * relay conversation — open a stream, fold each event into the last exchange, and make sure
 * the progress line always stops. That last part is the one worth keeping in view: a stopped
 * or truncated stream ends without a `done` event, so without the final pass the reader
 * watches the working message forever with nothing to explain it.
 */
export async function streamAnswer(
  question: string,
  history: Turn[],
  mode: Mode,
  active: string[] | null,
  signal: AbortSignal,
  setExchanges: React.Dispatch<React.SetStateAction<Exchange[]>>,
  onActive: (set: string[]) => void,
): Promise<void> {
  try {
    for await (const event of ask(question, history, signal, mode, active)) {
      // Only a finished answer moves the version row: a failed or stopped one leaves it as it was.
      // Taken from the set the server resolved, not the one sent, so a code it dropped
      // (unknown, unlicensed, not text) never joins the row — plus every version a tool read,
      // which is how asking about a translation adds it.
      const after = event.type === 'done' ? setAfterAnswer(active, event.summary) : null
      if (after) onActive(after)
      setExchanges((current) => applyEvent(current, event))
    }
  } catch {
    if (!signal.aborted) {
      setExchanges((current) =>
        withLast(current, { failure: { message: t('error.generic'), kind: 'internal' } }),
      )
    }
  } finally {
    setExchanges((current) => {
      const last = current.at(-1)
      if (!last || !isWorking(last)) return current
      return withLast(current, {
        failure: signal.aborted
          ? { message: t('chat.stopped'), kind: 'internal' }
          : { message: t('error.incomplete'), kind: 'provider' },
      })
    })
  }
}

/**
 * The conversation: what has been asked, what is in flight, and how to add to it.
 *
 * History lives here, in the browser, because the relay is stateless — which is what lets
 * the API scale without a session store and is the cheapest protection there is for what is
 * GDPR Article 9 data.
 *
 * Retrying reuses the exchange already on screen rather than appending a new one, so a
 * failed attempt does not stay in the transcript, and it replays the history from *before*
 * that exchange — the answers after it were built on the one being replaced.
 */
export function useConversation(onActive: (set: string[]) => void): {
  exchanges: Exchange[]
  busy: boolean
  askQuestion: (question: string, mode: Mode, active: string[] | null) => Promise<void>
  retryLast: (mode: Mode, active: string[] | null) => void
  stop: () => void
} {
  const [exchanges, setExchanges] = useState<Exchange[]>([])
  const [busy, setBusy] = useState(false)
  const abort = useRef<AbortController | null>(null)

  const run = useCallback(
    async (
      question: string,
      history: Turn[],
      mode: Mode,
      active: string[] | null,
      retry: boolean,
    ) => {
    setBusy(true)
    const fresh = { prose: '', summary: null, retrieving: null, failure: null }
    setExchanges((current) =>
      retry ? withLast(current, fresh) : [...current, { question, ...fresh }],
    )

    const controller = new AbortController()
    abort.current = controller
    try {
      await streamAnswer(question, history, mode, active, controller.signal, setExchanges, onActive)
    } finally {
      setBusy(false)
      abort.current = null
    }
    },
    [onActive],
  )

  function historyBefore(count: number): Turn[] {
    return exchanges.slice(0, count).flatMap((exchange) => [
      { role: 'user' as const, content: exchange.question },
      { role: 'assistant' as const, content: exchange.prose },
    ])
  }

  return {
    exchanges,
    busy,
    askQuestion: (question: string, mode: Mode, active: string[] | null) =>
      run(question, historyBefore(exchanges.length), mode, active, false),
    // Retried in whatever mode is selected now, which is the same rule the
    // relay applies: the mode of this ask governs this answer, and a retry is
    // an ask. Replaying the failed one's mode would resurrect a choice the
    // reader may have changed while looking at the failure.
    retryLast: (mode: Mode, active: string[] | null) => {
      const last = exchanges.at(-1)
      if (!last || busy) return
      void run(last.question, historyBefore(exchanges.length - 1), mode, active, true)
    },
    stop: () => {
      abort.current?.abort()
      setBusy(false)
    },
  }
}


function applyEvent(exchanges: Exchange[], event: ChatEvent): Exchange[] {
  switch (event.type) {
    case 'retrieving':
      return withLast(exchanges, { retrieving: event.tool })
    case 'delta':
      return withLast(exchanges, {
        prose: (exchanges.at(-1)?.prose ?? '') + event.text,
        retrieving: null,
      })
    case 'done':
      return withLast(exchanges, { summary: event.summary, retrieving: null })
    case 'error':
      return withLast(exchanges, {
        failure: { message: event.message, kind: event.kind },
        retrieving: null,
      })
  }
}

function withLast(exchanges: Exchange[], patch: Partial<Exchange>): Exchange[] {
  // `at(-1)` narrows to `Exchange | undefined`, which the guard then removes — where
  // indexing needed a non-null assertion to say the same thing, and an assertion is a
  // claim the compiler cannot check.
  const last = exchanges.at(-1)
  if (last === undefined) return exchanges
  return [...exchanges.slice(0, -1), { ...last, ...patch }]
}

